import React, { useState } from 'react';
import axios from 'axios';
import './FileUpload.css';

export interface UploadedFileInfo {
    url: string;
    publicId: string;
    name: string;
    size: number;
    type: string;
}

interface FileUploadProps {
    onUpload: (file: UploadedFileInfo) => void;
    accept?: string;
    label?: string;
    disabled?: boolean;
    maxSizeBytes?: number;
    onUploadStateChange?: (
        isUploading: boolean
    ) => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({
    onUpload,
    accept = 'image/*,.pdf,.doc,.docx,.xlsx,.pptx,.txt,.csv',
    label = 'Upload File',
    disabled = false,
    maxSizeBytes = 10 * 1024 * 1024,
    onUploadStateChange
}) => {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');

    const cloudName =
        process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'mrtej3fz';

    const uploadPreset =
        process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'lets-change-zambia';

    const folder =
        process.env.REACT_APP_CLOUDINARY_FOLDER || 'lets-change-zambia';

    const handleFileChange = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = e.target.files?.[0];

        if (!file) {
            onUploadStateChange?.(false);
            return;
        }

        if (file.size > maxSizeBytes) {
            const maxSizeMB = Math.round(
                maxSizeBytes / (1024 * 1024)
            );

            setError(
                `File size must be ${maxSizeMB}MB or less`
            );
            onUploadStateChange?.(false);
            return;
        }

        if (!cloudName) {
            setError('Cloudinary cloud name is not configured');
            onUploadStateChange?.(false);
            return;
        }

        setUploading(true);
        onUploadStateChange?.(true);
        setError('');
        setProgress(0);

        try {
            const formData = new FormData();

            formData.append('file', file);
            formData.append('upload_preset', uploadPreset);
            formData.append('folder', folder);

            const response = await axios.post(
                `https://api.cloudinary.com/v1_1/${cloudName}/upload`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) /
                                (progressEvent.total || 1)
                        );

                        setProgress(percentCompleted);
                    }
                }
            );

            const { secure_url, public_id } = response.data;

            onUpload({
                url: secure_url,
                publicId: public_id,
                name: file.name,
                size: file.size,
                type: file.type || 'application/octet-stream'
            });

            setProgress(100);
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Upload failed'
            );
        } finally {
            setUploading(false);
            onUploadStateChange?.(false);
        }
    };

    return (
        <div className="file-upload">
            <label
                className={`upload-button${
                    disabled || uploading ? ' disabled' : ''
                }`}
            >
                {uploading ? `Uploading... ${progress}%` : label}

                <input
                    type="file"
                    accept={accept}
                    onChange={handleFileChange}
                    disabled={disabled || uploading}
                />
            </label>

            {error && (
                <span className="upload-error">
                    {error}
                </span>
            )}
        </div>
    );
};